import { useQuery } from '@apollo/client/react';

import {
  GET_CURRENT_PRECO_OFFER,
  GET_PRECO_ELIGIBILITY,
} from 'queries/prechargeoff';
import { hasPrecoOffer, isPrecoOfferInProgress } from 'utilities/helpers';
import { PRECO_OFFER_STATES } from 'utilities/constants';

const PRECO_ELIGIBLE_BUCKETS = [
  'PAST_DUE_121_150_DAYS',
  'PAST_DUE_151_180_DAYS',
];

export const PRECO_ENROLLMENT_STATUS = Object.freeze({
  ELIGIBLE: 'ELIGIBLE',
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
});

const usePrecoEnrollmentStatus = (account, { skip = false } = {}) => {
  const accountId = account?.id;
  const accountStatuses = account?.statuses || {};
  const mayHavePrecoOffer =
    accountStatuses.isPastDue || accountStatuses.isChargedOff;
  const isPrecoEligibleBucket =
    PRECO_ELIGIBLE_BUCKETS.includes(accountStatuses.pastDueStatus) &&
    !accountStatuses.isChargedOff;

  const { data: currentOfferData, loading: offerLoading } = useQuery(
    GET_CURRENT_PRECO_OFFER,
    {
      variables: { accountId },
      skip: skip || !accountId || !mayHavePrecoOffer,
      fetchPolicy: 'cache-and-network',
    },
  );

  const precoOffer = currentOfferData?.prechargeoffSettlementOffer;
  const hasOffer = hasPrecoOffer(precoOffer);
  const canCheckEligibility =
    !!currentOfferData && !hasOffer && isPrecoEligibleBucket;

  const { data: eligibilityData, loading: eligibilityLoading } = useQuery(
    GET_PRECO_ELIGIBILITY,
    {
      variables: { accountId },
      skip: skip || !accountId || !canCheckEligibility,
      fetchPolicy: 'no-cache',
    },
  );

  let enrollmentStatus = null;
  if (isPrecoOfferInProgress(precoOffer)) {
    enrollmentStatus = PRECO_ENROLLMENT_STATUS.ACTIVE;
  } else if (
    hasOffer &&
    precoOffer.data.offerState === PRECO_OFFER_STATES.COMPLETED
  ) {
    enrollmentStatus = PRECO_ENROLLMENT_STATUS.COMPLETED;
  } else if (
    canCheckEligibility &&
    eligibilityData?.precoEligibility?.eligible
  ) {
    enrollmentStatus = PRECO_ENROLLMENT_STATUS.ELIGIBLE;
  }

  const offerPending =
    isPrecoEligibleBucket && !currentOfferData && offerLoading;
  const eligibilityPending = canCheckEligibility && eligibilityLoading;

  return {
    enrollmentStatus,
    loading: offerPending || eligibilityPending,
  };
};

export default usePrecoEnrollmentStatus;
